// src/features/videos/videosSlice.ts
import {
    createSlice,
    createEntityAdapter,
    createAsyncThunk,
    createSelector,
    PayloadAction,
} from '@reduxjs/toolkit';
import type { RootState } from '@/store';
import {assetUrl, loadRuntimeJsonCache, saveRuntimeJsonCache} from '@/utils';
import type { VideoRecord, Status } from '@/types/video';
import { normalizeArray, toRecord } from '@/features/videos/videoNormalize';

/* ========================= Adapter & state ========================= */

const adapter = createEntityAdapter<VideoRecord>({

    sortComparer: (a, b) =>
        (b.releasedTs ?? b.releaseTs ?? 0) - (a.releasedTs ?? a.releaseTs ?? 0),
});

const initialState = adapter.getInitialState({
    status: 'idle' as Status,
    error: null as string | null,
    loadedAt: null as number | null,
});

/* ========================= Thunk ========================= */

const VIDEOS_JSON = 'json_videos.json';

export const loadVideos = createAsyncThunk<
    VideoRecord[],
    void,
    { rejectValue: string }
>('videos/load', async (_, { rejectWithValue }) => {
    try {
        const res = await fetch(assetUrl(VIDEOS_JSON), { cache: 'no-cache' });

        if (!res.ok) {
            throw new Error(`Network error: ${res.status}`);
        }

        const contentType = res.headers.get('content-type') ?? '';
        if (!contentType.toLowerCase().includes('application/json')) {
            // throw new Error(`Expected JSON, got ${contentType || 'unknown content type'}`);
        }

        const raw = await res.json();
        saveRuntimeJsonCache(VIDEOS_JSON, raw);

        return normalizeArray(raw).map(toRecord);
    } catch (e: any) {
        const cached = loadRuntimeJsonCache<unknown>(VIDEOS_JSON);

        if (cached) {
            return normalizeArray(cached).map(toRecord);
        }

        // return rejectWithValue(e?.message ?? 'Failed to load videos');
        return [];
    }
});

/* ========================= Slice ========================= */

export const videosSlice = createSlice({
    name: 'videos',
    initialState,
    reducers: {
        upsertVideo: adapter.upsertOne,
        upsertManyVideos: adapter.upsertMany,
        removeVideo: adapter.removeOne,
        resetVideos: (s) => {
            adapter.removeAll(s);
            s.status = 'idle';
            s.error = null;
            s.loadedAt = null;
        },
    },
    extraReducers: (b) => {
        b.addCase(loadVideos.pending, (s) => {
            s.status = 'loading';
            s.error = null;
        });
        b.addCase(loadVideos.fulfilled, (s, a: PayloadAction<VideoRecord[]>) => {
            s.status = 'succeeded';
            adapter.setAll(s, a.payload);
            s.loadedAt = Date.now();
        });
        b.addCase(loadVideos.rejected, (s, a) => {
            s.status = 'failed';
            s.error = (a.payload as string) ?? 'Unknown error';
        });
    },
});

export const { upsertVideo, upsertManyVideos, removeVideo, resetVideos } =
    videosSlice.actions;

export const videosReducer = videosSlice.reducer;

/* ========================= Selectors (с защитой) ========================= */

const empty = adapter.getInitialState({
    status: 'idle' as Status,
    error: null as string | null,
    loadedAt: null as number | null,
});

const base = (s: RootState) => {
    const slice = (s as any).videos;
    const ok = slice && Array.isArray(slice.ids) && typeof slice.entities === 'object';
    return ok ? slice : empty;
};

export const {
    selectAll: selectAllVideos,
    selectIds: selectVideoIds,
} = adapter.getSelectors<RootState>(base);

export const selectVideoById = (id?: number | string) =>
    createSelector([base], (s) => {
        if (id == null) return undefined;
        const n = Number(id);
        return Number.isFinite(n) ? s.entities[n] : undefined;
    });

export const selectVideoBySlug = (slug?: string) =>
    createSelector([selectAllVideos], (all) => {
        if (!slug) return undefined;
        const hit = all.find((v) => v.slug === slug);
        if (hit) return hit;
        const m = slug.match(/^(\d+)/);
        return m ? all.find((v) => String(v.id) === m[1]) : undefined;
    });

export const selectVideosStatus   = (s: RootState) => base(s).status;
export const selectVideosError    = (s: RootState) => base(s).error;
export const selectVideosLoadedAt = (s: RootState) => base(s).loadedAt;


export const selectUpcomingVideos = createSelector([selectAllVideos], (all) =>
    [...all]
        .filter((v) => v.upcoming || (v.releaseTs != null && v.releaseTs > Date.now()))
        .sort((a, b) => (a.releaseTs ?? Infinity) - (b.releaseTs ?? Infinity))
);


export const selectReleasedVideos = createSelector([selectAllVideos], (all) =>
    [...all]
        .filter((v) => !v.upcoming && ((v.releasedTs ?? v.releaseTs ?? 0) <= Date.now()))
        .sort(
            (a, b) =>
                (b.releasedTs ?? b.releaseTs ?? 0) -
                (a.releasedTs ?? a.releaseTs ?? 0)
        )
);


export type FilterMode = 'any' | 'all';
export const makeSelectVideosByMembers = (memberIds: Array<string | number>, mode: FilterMode) =>
    createSelector([selectAllVideos], (all) => {
        if (!memberIds.length) return all;
        const wanted = new Set(memberIds.map((x) => String(x)));
        return all.filter((v) => {
            const ids = (v.podcast_pro_ids ?? []).map((n) => String(n));
            if (!ids.length) return false;
            return mode === 'all'
                ? [...wanted].every((x) => ids.includes(x))
                : ids.some((x) => wanted.has(x));
        });
    });
